import React, { useEffect, useState } from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { Modal, Button, FormFeedback } from '@morressier/alembic';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePlus, faSearch } from '@fortawesome/pro-light-svg-icons';
import { RinggoldOrg } from './RinggoldSearch';
import { useFormContext } from 'react-hook-form';
import { IAuthorsValue } from 'src/components/Submission/GenericFieldComponents/Authors/AuthorsUpdateModal';
import AuthorRinggoldSearch from './AuthorRinggoldSearch';
import { AffiliationCountryList } from 'src/api/queries';

//TODO: User proper contribution types
interface Props {
  isOpen: boolean;
  onClose: () => void;
  index: number;
  onAdd: (value: string) => void;
}

const renderSearchFieldErrorMessage = (message?: string) => {
  let formattedMessage = message;

  switch (message) {
    case 'must match pattern "\\S"':
      formattedMessage = 'Affiliation cannot be blank';
      break;
    case 'must NOT have fewer than 3 characters':
      formattedMessage = 'Please type at least 3 characters to search';
      break;
    default:
      break;
  }

  return <FormFeedback variant="error">{formattedMessage}</FormFeedback>;
};

const AffiliationModal: React.FC<Props> = ({
  isOpen,
  onClose,
  index,
  onAdd
}) => {
  const {
    formState: { errors },
    register,
    setValue,
    clearErrors,
    getValues,
    setError,
    trigger
  } = useFormContext<IAuthorsValue>();
  const fieldError = errors.metadata?.authors?.[index];
  const authorData = getValues(`metadata.authors.${index}`);
  const { affiliation, country, countryCode, city, postalCode } = authorData;
  const author = getValues(`metadata.authors.${index}`);
  const [isVerified, setIsVerified] = useState(false);
  const [isAddAffiliationManually, setIsAddAffiliationManually] = useState(
    author.ringgoldId !== '' ? false : true
  );

  const defaultAffiliation = {
    country: country ?? '',
    countryCode: countryCode ?? ''
  };

  const [selectedCountry, setSelectedCountry] =
    useState<AffiliationCountryList | null>(defaultAffiliation);
  const [preAffiliation, setPreAffiliation] = useState<string>(affiliation);
  const [preAffiliationCity, setPreAffiliationCity] = useState<
    string | undefined
  >(city);
  useEffect(() => {
    setPreAffiliation(affiliation);
    setPreAffiliationCity(preAffiliationCity);
  }, [isOpen]);
  const handleOrganizationSelection = (org: RinggoldOrg, index: number) => {
    onAdd(org.name);
    // update the selected org values in form
    setValue(`metadata.authors.${index}.affiliation`, org.name, {
      shouldValidate: true,
      shouldDirty: true
    });
    setValue(`metadata.authors.${index}.ringgoldId`, org.ringgoldId, {
      shouldValidate: true,
      shouldDirty: true
    });
    setValue(`metadata.authors.${index}.country`, org!.country ?? '', {
      shouldValidate: true,
      shouldDirty: true
    });
    setValue(`metadata.authors.${index}.countryCode`, org!.countryCode ?? '', {
      shouldValidate: true,
      shouldDirty: true
    });
    setValue(`metadata.authors.${index}.city`, org!.city ?? '', {
      shouldValidate: true,
      shouldDirty: true
    });
    setValue(`metadata.authors.${index}.postalCode`, org!.postCode ?? '', {
      shouldValidate: true,
      shouldDirty: true
    });
  };

  const handleRinggoldChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const value = e.target.value;
    setValue(`metadata.authors.${index}.affiliation`, value, {
      shouldDirty: true
    });
    trigger(`metadata.authors.${index}.affiliation`);
    // if input value is cleared in that case reset the ringgoldId as well
    if (!value.trim().length) {
      setValue(`metadata.authors.${index}.ringgoldId`, undefined, {
        shouldValidate: true,
        shouldDirty: true
      });
    }
  };

  const handleApply = () => {
    if (affiliation.trim() === '') {
      setError(`metadata.authors.${index}.affiliation`, {
        message: 'Please search an organization name.'
      });
      return;
    }
    if (city?.startsWith(' ')) {
      setError(`metadata.authors.${index}.city`, {
        message: 'Enter a city name without leading spaces.'
      });
      return;
    }
    if (postalCode?.startsWith(' ')) {
      setError(`metadata.authors.${index}.postalCode`, {
        message: 'Enter a postalCode without leading spaces.'
      });
      return;
    }
    setPreAffiliation(affiliation);
    setIsAddAffiliationManually(false);
    setIsVerified(false);
    trigger('metadata.authors');
    onClose();
  };
  const handleAddUnverifiedAffiliations = () => {
    const affiliation = getValues(`metadata.authors.${index}.affiliation`);
    const city = getValues(`metadata.authors.${index}.city`);
    const postalCode = getValues(`metadata.authors.${index}.postalCode`);
    let hasValidationError = false;

    if (affiliation.trim() === '') {
      setError(`metadata.authors.${index}.affiliation`, {
        message: 'Please enter an organization name.'
      });
      hasValidationError = true;
    }

    if (city?.startsWith(' ')) {
      setError(`metadata.authors.${index}.city`, {
        message: 'Enter a city name without leading spaces.'
      });
      hasValidationError = true;
    } else if (!city) {
      setError(`metadata.authors.${index}.city`, {
        message: 'Please enter a city.'
      });
      hasValidationError = true;
    }

    if (!selectedCountry?.country) {
      setError(`metadata.authors.${index}.country`, {
        message: 'Please select a country.'
      });
      hasValidationError = true;
    }
    if (postalCode?.startsWith(' ')) {
      setError(`metadata.authors.${index}.postalCode`, {
        message: 'Enter a postalCode without leading spaces.'
      });
      hasValidationError = true;
    }

    if (hasValidationError || !selectedCountry?.country) return;

    setValue(`metadata.authors.${index}.country`, selectedCountry.country, {
      shouldValidate: true,
      shouldDirty: true
    });
    setValue(
      `metadata.authors.${index}.countryCode`,
      selectedCountry.countryCode,
      {
        shouldValidate: true,
        shouldDirty: true
      }
    );
    setValue(`metadata.authors.${index}.ringgoldId`, '', {
      shouldValidate: true,
      shouldDirty: true
    });
    setSelectedCountry(null);

    onAdd(affiliation);
    setPreAffiliation(affiliation);
    trigger('metadata.authors');
    onClose();
    setIsAddAffiliationManually(false);
    setIsVerified(false);
    clearErrors(`metadata.authors.${index}.affiliation`);
    clearErrors(`metadata.authors.${index}.country`);
  };

  const handleClose = () => {
    if (preAffiliation !== affiliation) {
      setValue(`metadata.authors.${index}.affiliation`, preAffiliation, {
        shouldDirty: false
      });
    }
    if (preAffiliationCity !== city) {
      setValue(`metadata.authors.${index}.city`, preAffiliationCity, {
        shouldDirty: false
      });
    }
    setPreAffiliation('');
    setPreAffiliationCity('');
    // update the selected org values in form
    clearErrors(`metadata.authors.${index}.affiliation`);
    clearErrors(`metadata.authors.${index}.city`);
    clearErrors(`metadata.authors.${index}.country`);
    setIsVerified(false);
    setIsAddAffiliationManually(false);
    trigger('metadata.authors');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      width="755px"
      data-cy="contribution-modal"
    >
      <Flex flexDirection="column" gap={6} padding={8}>
        <Flex flexDirection="row" alignItems="center" gap={2}>
          <FontAwesomeIcon icon={faFilePlus} size="2xl" />
        </Flex>
        <Flex flexDirection="column" gap={2}>
          <Text textStyle="headingGroup">Add Affiliation</Text>
          <Text textStyle="textBody">
            Add your affiliation by searching the Ringgold database. If you
            cannot find it, select “Add Affiliation Manually” and enter the
            details.
          </Text>
        </Flex>

        <Flex flexDirection="column">
          <AuthorRinggoldSearch
            {...register(`metadata.authors.${index}.affiliation`, {
              required: true
            })}
            placeholder="Search affiliations"
            iconLeft={<FontAwesomeIcon icon={faSearch} />}
            isInvalid={!!fieldError?.affiliation}
            onOrganizationSelect={org => {
              handleOrganizationSelection(org, index);
              setIsVerified(true);
            }}
            onChange={e => {
              handleRinggoldChange(e, index);
            }}
            onAddUnverifiedAffiliation={() => {
              clearErrors(`metadata.authors.${index}.affiliation`);
              // Reset the ringgoldId
              setValue(`metadata.authors.${index}.ringgoldId`, undefined);
            }}
            index={index}
            isAddAffiliationManually={isAddAffiliationManually}
            setIsAddAffiliationManually={setIsAddAffiliationManually}
            selectedCountry={selectedCountry}
            setSelectedCountry={setSelectedCountry}
            isVerified={isVerified}
          />
          {errors?.metadata?.authors?.[index]?.affiliation &&
            !isAddAffiliationManually &&
            renderSearchFieldErrorMessage(
              errors?.metadata?.authors?.[index]?.affiliation?.message
            )}
        </Flex>

        <Flex flexDirection="row" gap={2} justifyContent="end">
          <Button
            variant="secondary"
            onClick={handleClose}
            data-cy="cancel-button"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!isVerified && !isAddAffiliationManually}
            onClick={
              isAddAffiliationManually
                ? handleAddUnverifiedAffiliations
                : handleApply
            }
            data-cy="apply-button"
          >
            Add Affiliation
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};

export default AffiliationModal;
